export interface ClientConfig {
  devMode: boolean;
  workerBaseUrl: string;
}

function isLocalhost(): boolean {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

// Worker base URL: relative /api path in production (same domain via Cloudflare routing)
// In local dev, point to separate worker instance on different port
// Can be overridden with VITE_API_BASE_URL environment variable if needed
function getWorkerBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl) {
    return envUrl;
  }
  // Auto-detect: localhost points to local worker, production uses /api same-origin path
  return isLocalhost() ? 'http://localhost:8787' : '/api';
}

let config: ClientConfig = {
  devMode: isLocalhost(),
  workerBaseUrl: getWorkerBaseUrl(),
};

/**
 * Get the current client configuration
 * Guaranteed to be valid at this point
 */
export const CONFIG: Readonly<ClientConfig> = new Proxy({} as ClientConfig, {
  get(_target, prop) {
    return config[prop as keyof ClientConfig];
  },
  set() {
    throw new Error('CONFIG is read-only. Use overrideConfigForTesting in tests.');
  },
});

/**
 * For testing only: override config values
 * @throws {Error} if not in test environment
 */
export function overrideConfigForTesting(overrides: Partial<ClientConfig>): () => void {
  if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'test') {
    throw new Error('overrideConfigForTesting is only available in test environments');
  }

  // Store original config
  const original = config;

  // Create new config with overrides
  config = { ...config, ...overrides };

  // Return restore function
  return () => {
    config = original;
  };
}
